import { ReactElement } from 'react';

import {
   MedicalTreatmentIndications,
   Specialty,
   SpecialtyQuery,
} from '@dreryk/egabinet-discharge-summary-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { FormContextValues } from '@dreryk/react-common';
import { Autosuggest } from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { dischargeSummaryTranslationKeys } from '../../../../translations';
import { FurtherMedicalTreatmentGuidelinesFormBeanPart } from '../FurtherMedicalTreatmentGuidelinesField/FurtherMedicalTreatmentGuidelinesField';

const specialtySearchCreator = (searchText: string | null): (() => Promise<Specialty[]>) => {
   return async (): CqrsQueryPerformResult<Specialty[]> =>
      performCqrsQuerySearch(() => {
         const medicalClinicQuery = new SpecialtyQuery();
         medicalClinicQuery.textMatch = TextPattern.anywhereIgnoreCase(searchText ?? '');
         return medicalClinicQuery;
      });
};

const itemRenderer = (item: Specialty): JSX.Element => {
   return (
      <>
         {item.name}
         {' - '}
         {item.code}
      </>
   );
};

const labelExtractor = (specialty: Specialty): string => specialty.name;

const APPLICABLE_FURTHER_MEDICAL_PROCEDURES_GUIDELINES: MedicalTreatmentIndications[] = [
   'SPECIALIST_CARE_NEEDED',
   'SPECIALIST_CARE_NOT_NEEDED',
];

type SpecialtyFormBeanPart = {
   specialty: Specialty | null;
};

type SpecialtyFieldProps = Pick<
   FormContextValues<SpecialtyFormBeanPart & FurtherMedicalTreatmentGuidelinesFormBeanPart>,
   'register' | 'watch'
>;

export const SpecialtyField = (props: SpecialtyFieldProps): ReactElement | null => {
   const furtherMedicalTreatmentGuidelines = props.watch(
      'furtherMedicalTreatmentGuidelines'
   ) as MedicalTreatmentIndications;

   if (!APPLICABLE_FURTHER_MEDICAL_PROCEDURES_GUIDELINES.includes(furtherMedicalTreatmentGuidelines)) {
      return null;
   }

   return (
      <Autosuggest<Specialty>
         testId={'specialty'}
         label={dischargeSummaryTranslationKeys.speciality}
         placeholder={dischargeSummaryTranslationKeys.searchByName}
         searchCreator={specialtySearchCreator}
         labelExtractor={labelExtractor}
         itemRenderer={itemRenderer}
         keyProperty={'id'}
         controller={props.register('specialty')}
      />
   );
};
